import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Check } from "lucide-react";
import { RevealLines, FadeUp } from "@/components/site/Reveal";
import { SERVICES } from "@/data/site";

export default function Services() {
  return (
    <div data-testid="services-page">
      {/* HEADER */}
      <section className="mx-auto max-w-[1400px] px-6 lg:px-12 pt-40 lg:pt-52 pb-16">
        <p className="mb-8 flex items-center gap-3 text-sm tracking-[0.25em] uppercase text-muted-foreground">
          <span className="h-px w-10 bg-violet" /> Nos services
        </p>
        <h1 className="font-display font-extrabold tracking-tighter leading-[0.9] text-6xl sm:text-7xl lg:text-8xl">
          <RevealLines lines={["Ce que nous", "savons faire."]} />
        </h1>
        <FadeUp delay={0.4}>
          <p className="mt-10 max-w-2xl text-lg text-muted-foreground leading-relaxed">
            Cinq expertises complémentaires, du diagnostic à la diffusion. Prestations digitales partout en France,
            tournages et prises de vue dans le Lot et les départements limitrophes.
          </p>
        </FadeUp>
      </section>

      {/* SERVICES SECTIONS */}
      {SERVICES.map((s, i) => (
        <section
          key={s.slug}
          id={s.slug}
          className="border-t border-black/10 py-16 lg:py-28"
          data-testid={`service-${s.slug}`}
        >
          <div className="mx-auto max-w-[1400px] px-6 lg:px-12 grid lg:grid-cols-12 gap-10 lg:gap-16">
            {/* Sticky title */}
            <div className="lg:col-span-5">
              <div className="lg:sticky lg:top-32">
                <span className="font-editorial text-2xl text-violet">{s.index} —</span>
                <h2 className="mt-4 font-display font-extrabold tracking-tighter text-4xl sm:text-5xl lg:text-6xl leading-[0.95]">
                  {s.title}
                </h2>
                <p className="mt-4 text-violet font-medium">{s.tagline}</p>
              </div>
            </div>

            {/* Content */}
            <div className="lg:col-span-7">
              <motion.div
                initial={{ clipPath: "inset(100% 0 0 0)" }}
                whileInView={{ clipPath: "inset(0% 0 0 0)" }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
                className="relative overflow-hidden aspect-[16/10] bg-secondary mb-8"
              >
                <img src={s.image} alt={s.title} className="h-full w-full object-cover" />
              </motion.div>
              <FadeUp>
                <p className="text-lg text-muted-foreground leading-relaxed">{s.description}</p>
                <ul className="mt-8 grid sm:grid-cols-2 gap-4">
                  {s.points.map((p) => (
                    <li key={p} className="flex items-center gap-3 border-b border-black/10 pb-3">
                      <Check size={18} className="text-violet shrink-0" />
                      <span className="font-medium">{p}</span>
                    </li>
                  ))}
                </ul>
              </FadeUp>
            </div>
          </div>
        </section>
      ))}

      {/* CTA */}
      <section className="border-t border-black/10 bg-ink text-white">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-12 pt-24 lg:pt-32 pb-12 lg:pb-16 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-10">
          <h2 className="font-display font-extrabold tracking-tighter text-4xl sm:text-5xl lg:text-6xl max-w-xl leading-[0.95]">
            Un besoin précis ? <span className="font-editorial italic font-normal text-violet-light">Discutons-en.</span>
          </h2>
          <Link
            to="/contact"
            className="group inline-flex items-center gap-3 bg-white text-ink px-8 py-4 hover:bg-violet hover:text-white transition-colors duration-300"
            data-testid="services-cta"
          >
            Demander un devis <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </section>
    </div>
  );
}
